import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { Button } from './ui/button';
import { Gift, BookOpen, Radio, Bot, Trophy, Send } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const COLORS = ['#FFB800', 'var(--primary-color)', '#FFFFFF', '#050505', '#D4AF37', '#10B981'];

function ConfettiRain() {
    const pieces = useMemo(() => Array.from({ length: 70 }, (_, i) => ({
        id: i,
        left: Math.random() * 100,
        delay: Math.random() * 2.5,
        duration: 3 + Math.random() * 2.5,
        size: 6 + Math.random() * 8,
        color: COLORS[i % COLORS.length],
        rotate: Math.random() * 360,
        round: Math.random() > 0.6,
    })), []);
    return (
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
            {pieces.map((p) => (
                <span
                    key={p.id}
                    className="confetti-piece"
                    style={{
                        left: `${p.left}%`,
                        width: p.size,
                        height: p.size * (p.round ? 1 : 0.45),
                        backgroundColor: p.color,
                        borderRadius: p.round ? '50%' : '2px',
                        animationDelay: `${p.delay}s`,
                        animationDuration: `${p.duration}s`,
                        transform: `rotate(${p.rotate}deg)`,
                    }}
                />
            ))}
        </div>
    );
}

const UNLOCKED = [
    { Icon: BookOpen, key: 'courses' },
    { Icon: Radio, key: 'signals' },
    { Icon: Bot, key: 'assistant' },
    { Icon: Trophy, key: 'challenge' },
];

export function WelcomeCelebration({ user, onDone }) {
    const { t } = useTranslation();
    const [closing, setClosing] = useState(false);

    const dismiss = async () => {
        setClosing(true);
        try {
            await axios.post(`${API}/auth/celebrate-welcome`, {}, { withCredentials: true });
        } catch { /* non-blocking */ }
        onDone();
    };

    return (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4" style={{ backgroundColor: 'rgba(5,5,5,0.75)', backdropFilter: 'blur(6px)' }} data-testid="welcome-celebration">
            <ConfettiRain />
            <div className={`relative w-full max-w-md rounded-3xl p-8 text-center transition-all duration-300 ${closing ? 'opacity-0 scale-95' : 'opacity-100 scale-100'}`}
                style={{ backgroundColor: 'var(--background-base)', border: '1px solid var(--border-highlight)', boxShadow: '0 0 60px rgba(255,184,0,0.35)' }}>
                <div className="w-20 h-20 rounded-3xl flex items-center justify-center mx-auto mb-5 gold-glow animate-bounce" style={{ background: 'linear-gradient(135deg, #FFB800, #B8860B)' }}>
                    <Gift className="w-10 h-10 text-white" />
                </div>
                <p className="text-xs font-black tracking-[0.25em] uppercase mb-2" style={{ color: 'var(--primary-color)' }}>{t('celebration.badge')}</p>
                <h2 className="text-2xl sm:text-3xl font-black mb-2" style={{ fontFamily: "'Outfit', sans-serif", color: 'var(--text-primary)' }}>
                    {t('celebration.title', { name: (user?.name || '').split(' ')[0] })}
                </h2>
                <p className="text-sm mb-6 leading-relaxed" style={{ color: 'var(--text-secondary)' }}>
                    {t('celebration.text')}
                </p>
                <div className="grid grid-cols-4 gap-2 mb-6">
                    {UNLOCKED.map(({ Icon, key }) => (
                        <div key={key} className="rounded-xl p-3 flex flex-col items-center gap-1.5" style={{ backgroundColor: 'var(--background-surface)', border: '1px solid var(--border-subtle)' }}>
                            <Icon className="w-5 h-5" style={{ color: 'var(--primary-color)' }} />
                            <span className="text-[10px] font-semibold leading-tight" style={{ color: 'var(--text-secondary)' }}>{t(`locked.features.${key}`)}</span>
                        </div>
                    ))}
                </div>
                <p className="text-xs mb-5 flex items-center justify-center gap-1.5" style={{ color: 'var(--text-muted)' }}>
                    <Send className="w-3.5 h-3.5" /> {t('celebration.emailNote')}
                </p>
                <Button onClick={dismiss} className="btn-primary w-full py-6 h-auto text-base font-black" data-testid="celebration-start-btn">
                    {t('celebration.cta')} 🚀
                </Button>
            </div>
        </div>
    );
}
